/**
 * SMS gate shown after the personal step, before review. It sends the code on
 * arrival (unless a challenge is already cached), resends under a cooldown,
 * and hands the verification token back once the digits check out.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  otpErrorCode,
  resendCourierRegistrationOtp,
  sendCourierRegistrationOtp,
  verifyCourierRegistrationOtp,
  type OtpErrorCode,
} from 'features/courier-onboarding/api/otp';
import type { SupportedLanguage } from 'shared/i18n/i18n';
import { cn } from 'shared/lib/utils';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

const ERROR_KEYS: Record<OtpErrorCode, string> = {
  OTP_INVALID: 'onboarding.otp.errors.invalid',
  OTP_EXPIRED: 'onboarding.otp.errors.expired',
  OTP_LOCKED: 'onboarding.otp.errors.locked',
  OTP_RESEND_COOLDOWN: 'onboarding.otp.errors.cooldown',
  OTP_NOT_FOUND: 'onboarding.otp.errors.notFound',
  OTP_SEND_FAILED: 'onboarding.otp.errors.sendFailed',
  UNKNOWN: 'onboarding.otp.errors.unknown',
};

interface PhoneVerificationPanelProps {
  phone: string;
  language: SupportedLanguage;
  challengeId: string | null;
  sentTo: string;
  onChallenge: (challengeId: string, sentTo: string) => void;
  onVerified: (token: string) => void;
  onChangePhone: () => void;
}

const PhoneVerificationPanel: React.FC<PhoneVerificationPanelProps> = ({
  phone,
  language,
  challengeId,
  sentTo,
  onChallenge,
  onVerified,
  onChangePhone,
}) => {
  const { t } = useTranslation();

  const [activeChallengeId, setActiveChallengeId] = useState<string | null>(
    challengeId,
  );
  const [digits, setDigits] = useState<string[]>(() =>
    Array(CODE_LENGTH).fill(''),
  );
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [errorKey, setErrorKey] = useState<string | undefined>();
  const [cooldown, setCooldown] = useState(0);

  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const code = digits.join('');

  const onChallengeRef = useRef(onChallenge);
  onChallengeRef.current = onChallenge;

  const request = useCallback(
    async (existingChallengeId: string | null) => {
      setSending(true);
      setErrorKey(undefined);
      try {
        const result = existingChallengeId
          ? await resendCourierRegistrationOtp(existingChallengeId)
          : await sendCourierRegistrationOtp(phone, language);
        setActiveChallengeId(result.challengeId);
        onChallengeRef.current(result.challengeId, result.sentTo || sentTo);
        setDigits(Array(CODE_LENGTH).fill(''));
        setCooldown(RESEND_COOLDOWN_SECONDS);
        inputsRef.current[0]?.focus();
      } catch (err) {
        setErrorKey(ERROR_KEYS[otpErrorCode(err)]);
      } finally {
        setSending(false);
      }
    },
    [phone, language, sentTo],
  );

  const sentOnceRef = useRef(false);
  useEffect(() => {
    if (challengeId || sentOnceRef.current) return;
    sentOnceRef.current = true;
    void request(null);
  }, [challengeId, request]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const handleVerify = useCallback(async () => {
    if (!activeChallengeId || code.length !== CODE_LENGTH || verifying) return;
    setVerifying(true);
    setErrorKey(undefined);
    try {
      const result = await verifyCourierRegistrationOtp(activeChallengeId, code);
      onVerified(result.verificationToken);
    } catch (err) {
      setErrorKey(ERROR_KEYS[otpErrorCode(err)]);
      setDigits(Array(CODE_LENGTH).fill(''));
      inputsRef.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  }, [activeChallengeId, code, verifying, onVerified]);

  useEffect(() => {
    if (code.length === CODE_LENGTH && !verifying && activeChallengeId) {
      void handleVerify();
    }
  }, [activeChallengeId, code, handleVerify, verifying]);

  const setDigitAt = (index: number, value: string) => {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleDigitChange = (index: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, '');
    if (!cleaned) {
      setDigitAt(index, '');
      return;
    }
    if (cleaned.length > 1) {
      setDigits((prev) => {
        const next = [...prev];
        cleaned
          .slice(0, CODE_LENGTH - index)
          .split('')
          .forEach((ch, offset) => {
            next[index + offset] = ch;
          });
        return next;
      });
      const landed = Math.min(index + cleaned.length, CODE_LENGTH - 1);
      inputsRef.current[landed]?.focus();
      return;
    }
    setDigitAt(index, cleaned);
    if (index < CODE_LENGTH - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      setDigitAt(index - 1, '');
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleVerify();
    }
  };

  const busy = sending || verifying;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8">
      <div className="flex flex-col items-center text-center">
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-haat-red-50 text-haat-red-600">
          <MessageSquare size={26} />
        </span>

        <div dir="ltr" className="flex items-center justify-center gap-2">
          {digits.map((digit, index) => (
            <input
              // The boxes are positional; the index is their identity.
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              ref={(el) => {
                inputsRef.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label={
                t('onboarding.otp.digitLabel', { index: index + 1 }) as string
              }
              maxLength={CODE_LENGTH}
              disabled={busy}
              value={digit}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className={cn(
                'h-12 w-11 rounded-xl border text-center text-lg font-bold text-gray-900',
                'focus:border-haat-red-600 focus:outline-none focus:ring-2 focus:ring-haat-red-600/40',
                'disabled:bg-gray-50 disabled:text-gray-400',
                errorKey ? 'border-red-300' : 'border-gray-300',
              )}
            />
          ))}
        </div>

        {errorKey && (
          <p
            role="alert"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-red-600"
          >
            <AlertTriangle size={14} />
            {t(errorKey)}
          </p>
        )}

        <button
          type="button"
          onClick={() => void handleVerify()}
          disabled={busy || code.length !== CODE_LENGTH}
          className="btn-primary mt-6 inline-flex w-full max-w-sm items-center justify-center gap-2"
        >
          {verifying ? (
            <>
              <span className="loading-spinner h-4 w-4 border-white/40 border-t-white" />
              {t('onboarding.otp.verifying')}
            </>
          ) : (
            t('onboarding.otp.verify')
          )}
        </button>

        <button
          type="button"
          onClick={() => void request(activeChallengeId)}
          disabled={busy || cooldown > 0}
          className="mt-3 text-sm font-medium text-haat-red-600 hover:text-haat-red-700 disabled:cursor-not-allowed disabled:text-gray-400"
        >
          {sending
            ? t('onboarding.otp.sending')
            : cooldown > 0
              ? t('onboarding.otp.resendIn', { seconds: cooldown })
              : t('onboarding.otp.resend')}
        </button>

        <button
          type="button"
          onClick={onChangePhone}
          className="mt-2 text-xs font-medium text-gray-500 hover:text-gray-700"
        >
          {t('onboarding.otp.changePhone')}
        </button>
      </div>
    </div>
  );
};

export default PhoneVerificationPanel;
